import { useCallback, useMemo, type ReactNode } from 'react';
import {
  SupportChat,
  SupportChatProvider,
  SupportChatTrigger,
  useSupportChatContext,
  type SupportContext,
  type SupportDiagnosticsProvider,
} from '@web-front/support-chat';

import { useAppSelector } from '../../hooks/redux';
import { selectCurrentToken, selectUser } from '../../store/auth.slice';

import { Pressable } from '../../shared/ui';

import { createMeetDiagnosticsProvider } from './createMeetDiagnosticsProvider';
import { useSupportMeetingWorkflow } from './useSupportMeetingWorkflow';
import { useSupportNavigationWorkflow } from './useSupportNavigationWorkflow';
import { useSupportWorkflowResume } from './useSupportWorkflowResume';

type MeetSupportChatProps = {
  source: 'video-prejoin' | 'video-room';
  roomName?: string;
  roomId?: string;
  participantIdentity?: string;
  connected?: boolean;
  diagnosticsProvider?: SupportDiagnosticsProvider;
  onRetryConnection?: () => void | Promise<void>;
};

const SUPPORT_API_BASE_URL = (import.meta as ImportMeta & { env?: Record<string, string> }).env
  ?.VITE_SUPPORT_API_BASE_URL;

function useMeetSupportConfig({
  source,
  roomName,
  roomId,
  participantIdentity,
  connected,
  diagnosticsProvider,
  onRetryConnection,
}: MeetSupportChatProps) {
  const token = useAppSelector(selectCurrentToken);
  const user = useAppSelector(selectUser);

  const getAccessToken = useCallback(() => token || null, [token]);
  const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
  const localDate = new Intl.DateTimeFormat('en-CA', { timeZone }).format(new Date());
  const meetingWorkflow = useSupportMeetingWorkflow(token || null, timeZone, localDate);
  const navigationWorkflow = useSupportNavigationWorkflow(token || null);
  const workflowResume = useSupportWorkflowResume(meetingWorkflow, navigationWorkflow);

  const context = useMemo<SupportContext>(
    () => ({
      source,
      viewer: {
        role: user ? 'psychologist' : 'client',
        category: user?.category || undefined,
      },
      user: user
        ? {
            id: user.id != null ? String(user.id) : undefined,
            displayName: [user.first_name, user.last_name].filter(Boolean).join(' ') || user.email || undefined,
          }
        : undefined,
      video: {
        roomId: roomId || roomName,
        roomName,
        participantIdentity,
        connected,
      },
      app: {
        environment: (import.meta as ImportMeta & { env?: Record<string, string> }).env?.MODE || 'production',
      },
      scheduling: { timeZone, localDate },
    }),
    [connected, localDate, participantIdentity, roomId, roomName, source, timeZone, user],
  );

  const fallbackProvider = useMemo(
    () =>
      diagnosticsProvider ||
      createMeetDiagnosticsProvider({
        connected,
        participantIdentity,
      }),
    [connected, diagnosticsProvider, participantIdentity],
  );

  const resolvedApiBaseUrl =
    SUPPORT_API_BASE_URL ||
    ((import.meta as ImportMeta & { env?: Record<string, string> }).env?.MODE === 'production'
      ? (() => {
          throw new Error('VITE_SUPPORT_API_BASE_URL must be configured in production');
        })()
      : 'http://localhost:3040');

  return {
    context,
    apiBaseUrl: resolvedApiBaseUrl,
    getAccessToken,
    diagnosticsProvider: fallbackProvider,
    locale: 'ru' as const,
    onRetryConnection,
    meetingWorkflow,
    navigationWorkflow,
    workflowResume,
  };
}

export function MeetSupportChat(props: MeetSupportChatProps) {
  return <SupportChat {...useMeetSupportConfig(props)} />;
}

export function MeetSupportChatProvider({
  children,
  enabled = true,
  ...props
}: MeetSupportChatProps & { children: ReactNode; enabled?: boolean }) {
  if (!enabled) return <>{children}</>;

  return <EnabledMeetSupportChatProvider {...props}>{children}</EnabledMeetSupportChatProvider>;
}

function EnabledMeetSupportChatProvider({ children, ...props }: MeetSupportChatProps & { children: ReactNode }) {
  return (
    <SupportChatProvider {...useMeetSupportConfig(props)} panelPlacement="anchor" panelVariant="meet">
      {children}
    </SupportChatProvider>
  );
}

export function MeetSupportChatTrigger() {
  return (
    <SupportChatTrigger
      size={40}
      label={null}
      renderTrigger={({ isOpen, buttonProps }) => (
        <Pressable
          {...buttonProps}
          title={isOpen ? 'Закрыть поддержку' : 'Открыть поддержку'}
          className={`inline-flex h-[40px] w-[40px] shrink-0 items-center justify-center rounded-control transition-colors duration-150 ${
            isOpen ? 'ring-2 ring-accent ring-offset-1' : 'hover:opacity-85'
          }`}
        >
          <svg viewBox="0 0 48 48" className="h-[40px] w-[40px]" aria-hidden="true" focusable="false">
            <rect width="48" height="48" rx="12" fill="#C00000" />
            <circle cx="16" cy="15" r="2.3" fill="#fff" />
            <circle cx="24" cy="15" r="2.3" fill="#fff" />
            <circle cx="32" cy="15" r="2.3" fill="#fff" />
            <path d="M13.9 20h4.2v15h-4.2zM21.9 20h4.2v15h-4.2zM29.9 20h4.2v15h-4.2z" fill="#fff" />
          </svg>
        </Pressable>
      )}
    />
  );
}

export type MeetSupportChatMenuAction = {
  isOpen: boolean;
  /** Есть новый ответ специалиста в обращении (значок на кнопке «Ещё», когда иконки помощника на панели нет) */
  hasUnreadTicketReply: boolean;
  toggle: () => void;
};

/**
 * Даёт доступ к открытию чата ИИ-помощника из любого места внутри MeetSupportChatProvider
 * (например, из пункта меню «Ещё» на телефоне, где отдельной иконки помощника на панели нет).
 * На ширине до 640px панель чата открывается на весь экран и не привязана к кнопке-триггеру.
 */
export function MeetSupportChatMenuBridge({
  children,
}: {
  children: (action: MeetSupportChatMenuAction) => ReactNode;
}) {
  const chat = useSupportChatContext();

  return (
    <>
      {children({
        isOpen: chat.open,
        hasUnreadTicketReply: chat.hasUnreadTicketReply,
        toggle: () => {
          if (chat.open) chat.closeChat();
          else void chat.openChat();
        },
      })}
    </>
  );
}
